import { logError } from '@/lib/sentry.lib';

import { useNavigate } from 'react-router';

import { API } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { getNotifier } from '@repo/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteDraft, deleteQuotation } from '@/api/asset.api';
import { useAssetParams } from '@/hooks/asset.hooks';
import { useAppBaseUrl } from '@/hooks/router.hooks';

const getNotificationMessage = (assetType: string, assetId: string) => {
    if (assetType === 'draft') {
        return translate('dialog_delete_draft_order_notification_body', { number: assetId });
    }
    if (assetType === 'quote') {
        return translate('dialog_delete_quotation_order_notification_body', { number: assetId });
    }
    return '';
};

export const useDeleteAssetMutation = () => {
    const { assetId, assetType } = useAssetParams();
    const navigate = useNavigate();
    const notifier = getNotifier();

    const baseUrl = useAppBaseUrl();

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => {
            if (assetType === 'draft') {
                return deleteDraft(assetId);
            }
            if (assetType === 'quote') {
                return deleteQuotation(assetId);
            }
            return Promise.reject(new Error('Asset type not supported'));
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [API.assets] });
            void navigate(baseUrl);
            notifier?.add({
                title: translate('dialog_delete_notification_title'),
                message: getNotificationMessage(assetType, assetId),
            });
        },
        onError: (error) => {
            notifier?.add({
                title: translate('dialog_delete_notification_error_title'),
                message: translate('dialog_delete_notification_error_body'),
                type: 'error',
            });
            logError(error);
        },
    });
};
